// src/components/common/TimeZoneSelect.tsx

import React, { useState, useEffect } from 'react';
import { getUserData, updateUserTimeZone } from '../../services/auth';
import SelectInput from './SelectInput';

// 常用時區列表 (特別強調歐美地區和常用時間管理用戶)
const POPULAR_TIMEZONES = [
  { value: 'America/New_York', label: 'New York (UTC-5/UTC-4)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8/UTC-7)' },
  { value: 'America/Chicago', label: 'Chicago (UTC-6/UTC-5)' },
  { value: 'Europe/London', label: 'London (UTC/BST)' },
  { value: 'Europe/Paris', label: 'Paris (UTC+1/UTC+2)' },
  { value: 'Europe/Berlin', label: 'Berlin (UTC+1/UTC+2)' },
  { value: 'Asia/Taipei', label: 'Taipei (UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
  { value: 'Asia/Singapore', label: 'Singapore (UTC+8)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)' },
  { value: 'Australia/Sydney', label: 'Sydney (UTC+10/UTC+11)' }
];

// 時區區域分組
const TIMEZONE_REGIONS = {
  'Africa': [
    'Africa/Cairo', 'Africa/Casablanca', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi'
  ],
  'America': [
    'America/Anchorage', 'America/Argentina/Buenos_Aires', 'America/Bogota', 'America/Chicago',
    'America/Denver', 'America/Edmonton', 'America/Halifax', 'America/Los_Angeles', 'America/Mexico_City',
    'America/New_York', 'America/Phoenix', 'America/Santiago', 'America/Sao_Paulo', 'America/Toronto',
    'America/Vancouver'
  ],
  'Asia': [
    'Asia/Almaty', 'Asia/Baghdad', 'Asia/Bangkok', 'Asia/Beirut', 'Asia/Colombo', 'Asia/Dhaka',
    'Asia/Dubai', 'Asia/Hong_Kong', 'Asia/Istanbul', 'Asia/Jakarta', 'Asia/Jerusalem', 'Asia/Karachi',
    'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Manila', 'Asia/Qatar', 'Asia/Seoul', 'Asia/Shanghai',
    'Asia/Singapore', 'Asia/Taipei', 'Asia/Tehran', 'Asia/Tokyo', 'Asia/Vladivostok'
  ],
  'Australia': [
    'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Darwin', 'Australia/Hobart', 
    'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney'
  ],
  'Europe': [
    'Europe/Amsterdam', 'Europe/Athens', 'Europe/Belgrade', 'Europe/Berlin', 'Europe/Brussels',
    'Europe/Budapest', 'Europe/Copenhagen', 'Europe/Dublin', 'Europe/Helsinki', 'Europe/Istanbul',
    'Europe/Kiev', 'Europe/Lisbon', 'Europe/London', 'Europe/Madrid', 'Europe/Moscow',
    'Europe/Oslo', 'Europe/Paris', 'Europe/Prague', 'Europe/Rome', 'Europe/Stockholm',
    'Europe/Vienna', 'Europe/Warsaw', 'Europe/Zurich'
  ],
  'Pacific': [
    'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Guam', 'Pacific/Honolulu', 'Pacific/Samoa'
  ]
};

// 獲取時區所在區域
const getRegionForTimeZone = (timeZone: string): string => {
  for (const [region, zones] of Object.entries(TIMEZONE_REGIONS)) {
    if (zones.includes(timeZone)) {
      return region;
    }
  }
  // 以第一個 '/' 分割並返回區域
  const parts = timeZone.split('/');
  return parts.length > 1 ? parts[0] : 'Other';
};

// 格式化時區顯示名稱
const formatTimeZoneLabel = (timeZone: string): string => {
  // 從 '/' 後獲取城市名並替換下劃線為空格
  const parts = timeZone.split('/');
  const city = parts[parts.length - 1].replace(/_/g, ' ');
  
  // 獲取該時區當前的 UTC 偏移量
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      timeZoneName: 'longOffset'
    });
    const formatted = formatter.format(now);
    const match = formatted.match(/GMT([-+]\d{1,2}(?::\d{2})?)/);
    const offset = match ? match[1] : '';
    
    return `${city} (UTC${offset})`;
  } catch (e) {
    // 如果格式化失敗，只返回城市名
    return city;
  }
};

interface TimeZoneSelectProps {
  userId?: string;
  value?: string;
  onChange?: (timeZone: string) => void;
  className?: string;
  disabled?: boolean;
}

const TimeZoneSelect: React.FC<TimeZoneSelectProps> = ({ 
  userId, 
  value,
  onChange, 
  className = '',
  disabled = false
}) => {
  const [timeZone, setTimeZone] = useState<string>(value || '');
  const [loading, setLoading] = useState<boolean>(!value);
  const [error, setError] = useState<string | null>(null);
  const [allTimeZones, setAllTimeZones] = useState<string[]>([]);
  
  // 獲取所有可用時區
  useEffect(() => {
    try {
      // 嘗試獲取所有支持的時區
      const allZones: string[] = [];
      for (const region of Object.values(TIMEZONE_REGIONS)) {
        allZones.push(...region);
      }
      setAllTimeZones(allZones.sort());
    } catch (e) {
      console.error('Failed to get timezone list:', e);
    }
  }, []);
  
  // 如果沒有提供 value，則從用戶數據加載時區
  useEffect(() => {
    if (value) {
      setTimeZone(value);
      setLoading(false);
      return;
    }
    
    if (userId) {
      setLoading(true);
      getUserData(userId)
        .then(userData => {
          if (userData?.timeZone) {
            setTimeZone(userData.timeZone);
          } else {
            // 如果用戶沒有設置時區，使用瀏覽器默認時區
            setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
          }
          setError(null);
        })
        .catch(err => {
          console.error('Failed to get user timezone:', err);
          setError('Unable to load timezone setting');
          // 使用瀏覽器默認時區作為後備
          setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // 如果沒有提供用戶ID，使用瀏覽器默認時區
      setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      setLoading(false);
    }
  }, [userId, value]);
  
  const handleTimeZoneChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTimeZone = e.target.value;
    setTimeZone(newTimeZone);
    
    if (onChange) {
      onChange(newTimeZone);
    }
    
    // 如果提供了用戶ID，自動更新用戶時區
    if (userId) {
      try {
        setError(null);
        const success = await updateUserTimeZone(userId, newTimeZone);
        if (!success) {
          setError('Failed to update timezone');
        }
      } catch (err) {
        console.error('Error updating timezone:', err);
        setError('An error occurred while updating timezone');
      }
    }
  };
  
  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <select 
          disabled
          className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-gray-400 cursor-not-allowed"
        >
          <option>Loading...</option>
        </select>
      </div>
    );
  }
  
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  return (
    <div className={className}>
      <SelectInput
        value={timeZone}
        onChange={handleTimeZoneChange}
        disabled={disabled || loading}

      >
        {/* 選項保持不變 */}
        <option value={browserTimeZone}>
          Browser Default ({browserTimeZone.replace('_', ' ')})
        </option>
        
        {/* 常用時區列表 */}
        <optgroup label="Popular Timezones">
          {POPULAR_TIMEZONES.map(tz => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </optgroup>
        
        {/* 按區域分組的所有時區 */}
        {Object.entries(TIMEZONE_REGIONS).map(([region, zones]) => (
          <optgroup key={region} label={region}>
            {zones.map(zone => (
              <option key={zone} value={zone}>
                {formatTimeZoneLabel(zone)}
              </option>
            ))}
          </optgroup>
        ))}
      </SelectInput>
        
      <div className="text-xs text-neutral-500 mt-1">
        Current time: {new Date().toLocaleTimeString(undefined, { timeZone })}
      </div>
    </div>
  );
};

export default TimeZoneSelect;